import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Footer, Navbar } from "../components";
import MovieCardSkeleton from "../components/MovieCardSkeleton";
import { getAnimeShows, getTrendingAnime } from "../constants/api";
import { Link } from "react-router-dom";
import AdsBannerTop from "../components/AdsBannerTop";
import { Play, Star, Film } from "lucide-react";

const Anime = () => {
  const [trendingAnime, setTrendingAnime] = useState([]);
  const [allAnime, setAllAnime] = useState([]);
  const [pages, setPages] = useState(2);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchData = async (pageNumber, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const data = await getAnimeShows(pageNumber);
      return data?.results || [];
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Function to handle "Load More" button click
  const handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Save current scroll position
    const scrollPosition = window.scrollY;

    const nextPage = pages + 4;

    const dataForPage = await fetchData(nextPage, true);

    setAllAnime((prevAnime) => [
      ...(Array.isArray(prevAnime) ? prevAnime : []),
      ...dataForPage.map((anime) => ({ ...anime, page: nextPage })),
    ]);
    setPages(nextPage);
    
    // Restore scroll position after state update
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'instant'
      });
    }, 0);
  };

  useEffect(() => {
    const fetchTrendingData = async () => {
      const data = await getTrendingAnime();
      setTrendingAnime(data?.results || []);
    };

    fetchTrendingData();
  }, []);

  useEffect(() => {
    const fetchAllAnime = async () => {
      const dataForPage1 = await fetchData(pages - 1);
      const dataForPage2 = await fetchData(pages);
      const dataForPage3 = await fetchData(pages + 1);
      setAllAnime([
        ...dataForPage1.map((anime) => ({ ...anime, page: pages - 1 })),
        ...dataForPage2.map((anime) => ({
          ...anime,
          page: pages,
        })),
        ...dataForPage3.map((anime) => ({
          ...anime,
          page: pages + 1,
        })),
      ]);
    };

    fetchAllAnime();
  }, []);

  // JSX code for the component
  return (
    <>
      <Navbar />
      <AdsBannerTop />
      <div className="bg-black padding pt-[4rem]">
        {/* Section Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-1 w-12 bg-red"></div>
            <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
              Anime
            </span>
          </div>
          <h2 className="text-white mont font-bold lg:text-[42px] md:text-[36px] text-[28px] mb-1">
            Trending Anime
          </h2>
          <p className="text-white/60 mont text-[15px]">
            Discover the latest and most popular anime series
          </p>
        </div>

        {/* Modern Swiper component for "Trending Anime" */}
        <div className="relative mb-12">
          <Swiper
            spaceBetween={20}
            slidesPerView={5}
            autoplay={{ 
              delay: 3000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            loop={trendingAnime.length > 5}
            navigation={true}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            breakpoints={{
              230: {
                slidesPerView: 2,
                spaceBetween: 8,
              },
              480: {
                slidesPerView: 3,
                spaceBetween: 10,
              },
              640: {
                slidesPerView: 3,
                spaceBetween: 12,
              },
              768: {
                slidesPerView: 4,
                spaceBetween: 15,
              },
              1000: {
                slidesPerView: 5,
                spaceBetween: 18,
              },
              1280: {
                slidesPerView: 6,
                spaceBetween: 20,
              },
            }}
            modules={[Pagination, Autoplay, Navigation]}
            className="modern-swiper"
          >
            {trendingAnime?.filter(anime => anime.poster_path).map((anime) => (
              <SwiperSlide key={anime?.id} className="modern-movie-slide">
                <Link to={`/anime/${anime?.id}`} className="group">
                  <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                    {/* Anime Poster */}
                    <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                      <img
                        src={`https://image.tmdb.org/t/p/w500${anime.poster_path}`}
                        alt={anime?.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      {/* Placeholder when image fails */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray/40 to-gray/20 p-4" style={{ display: 'none' }}>
                        <Film className="text-white/40 mb-2" size={48} />
                        <p className="text-white/60 mont text-xs text-center line-clamp-2">{anime?.name}</p>
                      </div>
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                          <Play className="text-white" size={32} fill="white" />
                        </div>
                      </div>

                      {/* Rating Badge */}
                      <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm rounded-full px-2.5 py-1 flex items-center gap-1">
                        <Star className="text-yellow-400" size={14} fill="#FBBF24" />
                        <span className="text-white mont text-xs font-semibold">
                          {anime?.vote_average?.toFixed(1)}
                        </span>
                      </div>
                    </div>

                    {/* Anime Info */}
                    <div className="p-3">
                      <h3 className="text-white mont font-semibold text-[15px] leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                        {anime?.name}
                      </h3>
                      {anime?.first_air_date && (
                        <p className="text-white/50 mont text-xs mt-1">
                          {new Date(anime.first_air_date).getFullYear()}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* "All Anime" section */}
        <div className="mb-8 mt-16">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-1 w-12 bg-red"></div>
            <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
              Popular
            </span>
          </div>
          <h2 className="text-white mont font-bold lg:text-[42px] md:text-[36px] text-[28px] mb-1">
            All Anime
          </h2>
          <p className="text-white/60 mont text-[15px]">
            Explore our complete collection of anime series
          </p>
        </div>

        <div className="grid lg:grid-cols-6 md:grid-cols-4 sm:grid-cols-3 grid-cols-2 gap-4 md:gap-6">
          {loading && allAnime.length === 0 ? (
            // Show skeletons on initial load
            Array.from({ length: 12 }).map((_, index) => (
              <MovieCardSkeleton key={index} />
            ))
          ) : (
            <>
              {allAnime?.filter(anime => anime.poster_path).map((anime) => (
                <Link
                  key={anime?.id}
                  to={`/tvshows/${anime?.id}`}
                  className="group modern-movie-card"
                >
                  <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                    {/* Anime Poster */}
                    <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                      <img
                        src={`https://image.tmdb.org/t/p/w500${anime?.poster_path}`}
                        alt={anime?.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      {/* Placeholder when image fails */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray/40 to-gray/20 p-4" style={{ display: 'none' }}>
                        <Film className="text-white/40 mb-2" size={40} />
                        <p className="text-white/60 mont text-xs text-center line-clamp-2">{anime?.name}</p>
                      </div>
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                          <Play className="text-white" size={28} fill="white" />
                        </div>
                      </div>

                      {/* Rating Badge */}
                      <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                        <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                        <span className="text-white mont text-xs font-semibold">
                          {anime?.vote_average?.toFixed(1)}
                        </span>
                      </div>
                    </div>

                    {/* Anime Info */}
                    <div className="p-3">
                      <h3 className="text-white mont font-semibold text-[14px] leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                        {anime?.name}
                      </h3>
                      {anime?.first_air_date && (
                        <p className="text-white/50 mont text-xs mt-1">
                          {new Date(anime.first_air_date).getFullYear()}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
              
              {/* Show skeleton loaders when loading more */}
              {loadingMore && Array.from({ length: 6 }).map((_, index) => (
                <MovieCardSkeleton key={`skeleton-${index}`} />
              ))}
            </>
          )}
        </div>

        {/* Modern Load More Button */}
        <div className="flex justify-center mt-12">
          <button
            type="button"
            className="group relative overflow-hidden px-8 py-3 bg-red text-white mont font-semibold text-lg rounded-lg hover:bg-red/90 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red/30"
            onClick={handleClick}
            disabled={loadingMore}
          >
            <span className="relative z-10 flex items-center gap-2">
              {loadingMore ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Loading...
                </>
              ) : (
                <>
                  Load More Anime
                  <Play className="ml-1" size={18} fill="white" />
                </>
              )}
            </span>
            <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-300"></div>
          </button>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Anime;
